import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { LEGAL_DOCS, LEGAL_STATUS, LEGAL_LAST_UPDATED } from '../config/legal';
import Seo from '../components/Seo';

const heading = { fontFamily: "'Outfit', sans-serif" };

// Public legal page renderer for /privacy, /terms, /disclosures.
export default function LegalPage({ type }) {
  const params = useParams();
  const key = type || params.type;
  const doc = LEGAL_DOCS[key];

  if (!doc) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#061530] text-white">
        <div className="text-center">
          <p className="text-xl mb-4" style={heading}>Document not found</p>
          <Link to="/" className="text-[#C0975C] underline">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-[#061530] text-[#F5F1E8]" data-testid={`legal-${key}`}>
      <Seo
        title={`${doc.title} — Precision Wealth`}
        description={doc.intro}
        path={`/${key}`}
      />
      <div className="max-w-3xl mx-auto px-6 py-14">
        <Link to="/" className="inline-flex items-center gap-2 text-sm mb-8 text-[#94A9C9] hover:text-white transition-colors" data-testid="legal-back">
          <ArrowLeft className="w-4 h-4" /> Back to Precision Wealth
        </Link>

        <div className="text-xs font-semibold tracking-[0.3em] uppercase mb-3" style={{ color: '#C0975C' }}>Precision Wealth</div>
        <h1 className="text-4xl md:text-5xl font-bold mb-3" style={heading}>{doc.title}</h1>

        {LEGAL_STATUS !== 'APPROVED' && (
          <div className="flex items-start gap-2 rounded-xl px-4 py-3 mb-6 text-sm" style={{ backgroundColor: 'rgba(192,151,92,0.12)', border: '1px solid rgba(192,151,92,0.35)', color: '#E9D9BC' }} data-testid="legal-draft-banner">
            <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-[#C0975C]" />
            <span>DRAFT — this document is pending compliance and legal approval. Items marked "[To be confirmed]" must be completed before publication.</span>
          </div>
        )}

        <p className="text-[15px] leading-relaxed text-[#94A9C9] mb-2">{doc.intro}</p>
        <p className="text-xs text-[#6B7A99] mb-10">Last updated: {LEGAL_LAST_UPDATED}</p>

        <div className="space-y-8">
          {doc.sections.map((s) => (
            <section key={s.h}>
              <h2 className="text-lg md:text-xl font-bold mb-2 text-white" style={heading}>{s.h}</h2>
              <p className="text-[15px] leading-relaxed text-[#c6d0e0]">{s.b}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 pt-6 flex flex-wrap gap-4 text-sm" style={{ borderTop: '1px solid #1D3D72' }}>
          <Link to="/privacy" className="text-[#C0975C] hover:underline">Privacy</Link>
          <Link to="/terms" className="text-[#C0975C] hover:underline">Terms</Link>
          <Link to="/disclosures" className="text-[#C0975C] hover:underline">Disclosures</Link>
        </div>
      </div>
    </main>
  );
}
